// Dashboard — "Insurance payments posted" (H11 §1, D286). Insurance payments posted in the PMS
// this period, by anyone, net of reversals and undos; "of which ELVA posted" is the part ELVA
// posted itself. Never "paid", "collected" or "received": it is what was posted, not what is in
// the bank.
//
// The figures are summed from a small deterministic list of posting rows, never typed. Posting ›
// Posted reads the month total from getPostedThisMonth(), so the two pages always show the same
// number and the same "of which ELVA posted".

import { PAYERS, money, pick, rng } from '@/components/lists';

export type PostedPeriod = 'today' | 'week' | 'month';

export interface PostedRow {
  id: string;
  /** ISO date the payment was posted in the PMS. */
  dateIso: string;
  source: 'elva' | 'staff';
  payer: string;
  /** Negative for a reversal or an undo. */
  amount: number;
  /** A posted payment later proven not to have arrived (a returned check). */
  provedNotReceived?: boolean;
}

/** First ISO day of each period (inclusive), up to the Dashboard's mock today. */
const FROM_ISO: Record<PostedPeriod, string> = {
  today: '2026-10-15',
  week: '2026-10-12',
  month: '2026-10-01',
};
const TODAY_ISO = '2026-10-15';

interface Segment {
  days: string[];
  /** Net total of the segment and the part of it ELVA posted (mock calibration; the sums below are computed). */
  total: number;
  elva: number;
  elvaRows: number;
  staffRows: number;
  /** Reversals / undos inside the segment (positive numbers). */
  elvaUndone?: number;
  staffReversed?: number;
  /** A fixed staff-posted check inside the segment. */
  fixedCheck?: number;
}

const SEGMENTS: Segment[] = [
  { days: ['2026-10-15'], total: 6120, elva: 3410, elvaRows: 6, staffRows: 5 },
  { days: ['2026-10-12', '2026-10-13', '2026-10-14'], total: 25120, elva: 13950, elvaRows: 22, staffRows: 18 },
  {
    days: ['2026-10-01', '2026-10-02', '2026-10-05', '2026-10-06', '2026-10-07', '2026-10-08', '2026-10-09'],
    total: 42940,
    elva: 23900,
    elvaRows: 40,
    staffRows: 28,
    elvaUndone: 310,
    staffReversed: 480,
    fixedCheck: 5000,
  },
];

/** Scale `raw` so it adds up to `target` exactly, at cent precision. */
function scale(raw: number[], target: number): number[] {
  const sum = raw.reduce((s, v) => s + v, 0);
  const out = raw.map((v) => Math.round(((v * target) / sum) * 100) / 100);
  const drift = Math.round((target - out.reduce((s, v) => s + v, 0)) * 100) / 100;
  const last = out.length - 1;
  out[last] = Math.round(((out[last] ?? 0) + drift) * 100) / 100;
  return out;
}

function build(): PostedRow[] {
  const r = rng(0x7a11);
  const rows: PostedRow[] = [];
  let n = 0;
  const add = (row: Omit<PostedRow, 'id'>) => {
    n += 1;
    rows.push({ id: `pst-${String(n)}`, ...row });
  };
  for (const seg of SEGMENTS) {
    const elvaGross = seg.elva + (seg.elvaUndone ?? 0);
    const staffNet = seg.total - seg.elva;
    const staffGross = staffNet + (seg.staffReversed ?? 0);
    const staffGenerated = staffGross - (seg.fixedCheck ?? 0);
    const elvaAmounts = scale(Array.from({ length: seg.elvaRows }, () => money(r, 120, 2400)), elvaGross);
    const staffAmounts = scale(Array.from({ length: seg.staffRows }, () => money(r, 120, 2400)), staffGenerated);
    elvaAmounts.forEach((amount, i) => {
      add({ dateIso: seg.days[i % seg.days.length] ?? TODAY_ISO, source: 'elva', payer: pick(r, PAYERS), amount });
    });
    staffAmounts.forEach((amount, i) => {
      add({ dateIso: seg.days[(i + 1) % seg.days.length] ?? TODAY_ISO, source: 'staff', payer: pick(r, PAYERS), amount });
    });
    if (seg.fixedCheck) {
      // The one check that `?scenario=returned-check` proves never arrived. It stays in the posted
      // figure: the money was posted in the PMS; it just isn't in the bank.
      add({ dateIso: seg.days[2] ?? TODAY_ISO, source: 'staff', payer: 'Guardian', amount: seg.fixedCheck, provedNotReceived: true });
    }
    if (seg.elvaUndone) add({ dateIso: seg.days[3] ?? TODAY_ISO, source: 'elva', payer: 'Aetna', amount: -seg.elvaUndone });
    if (seg.staffReversed) add({ dateIso: seg.days[4] ?? TODAY_ISO, source: 'staff', payer: 'Cigna', amount: -seg.staffReversed });
  }
  return rows;
}

const ROWS: PostedRow[] = build();

/** `?scenario=returned-check`: a posted payment was proven not to have arrived. */
export function isReturnedCheckScenario(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('scenario') === 'returned-check';
  } catch {
    return false;
  }
}

export function getPostedRows(): PostedRow[] {
  return ROWS;
}

function inPeriod(row: PostedRow, period: PostedPeriod): boolean {
  return row.dateIso >= FROM_ISO[period] && row.dateIso <= TODAY_ISO;
}

export interface PostedFigures {
  /** Insurance payments posted by anyone, net of reversals and undos. */
  total: number;
  /** Of which ELVA posted. */
  elvaPosted: number;
  /** Posted payments proven not to have arrived. 0 unless `?scenario=returned-check`. The posted figure is NOT reduced by it. */
  confirmedNotReceived: number;
}

function sum(rows: PostedRow[]): number {
  return Math.round(rows.reduce((s, row) => s + row.amount, 0) * 100) / 100;
}

export function getPostedForPeriod(period: PostedPeriod): PostedFigures {
  const rows = ROWS.filter((row) => inPeriod(row, period));
  const returned = isReturnedCheckScenario();
  return {
    total: sum(rows),
    elvaPosted: sum(rows.filter((row) => row.source === 'elva')),
    confirmedNotReceived: returned ? sum(rows.filter((row) => row.provedNotReceived)) : 0,
  };
}

/**
 * This month's insurance payments posted: `{ total, elvaPosted }`. Posting › Posted's month total
 * reads this, so Dashboard and Posting always agree.
 */
export function getPostedThisMonth(): { total: number; elvaPosted: number } {
  const { total, elvaPosted } = getPostedForPeriod('month');
  return { total, elvaPosted };
}
